/**
 * bi-directional snap once scroll between a hero (full-height) and the main
 * content that follows it. works with wheel, touch, and keyboard.
 */

import { useCallback, useEffect, useRef } from "react";

// helpers //////////////////////////////////////////

function easeOutCubic(t: number) {
    return 1 - Math.pow(1 - t, 3);
}

function easeInOutQuart(t: number) {
    return t < 0.5
        ? 8 * t * t * t * t
        : 1 - Math.pow(-2 * t + 2, 4) / 2;
}

interface IAnimationOptions {
    duration?: number;
    easing?: 'cubic' | 'quart';
    animatingRef: React.MutableRefObject<boolean>;
    onDone?: () => void;
}

function animateWindowScrollTo(toY: number, opts: IAnimationOptions) {
    if (typeof window === "undefined") {
        return;
    }

    const duration = opts.duration ?? 900;
    const easingFn = opts.easing === 'quart' ? easeInOutQuart : easeOutCubic;
    const startY = window.scrollY || window.pageYOffset;
    const dist = toY - startY;
    const start = performance.now();
    opts.animatingRef.current = true;

    function step(now: number) {
        const t = Math.min(1, (now - start) / duration);
        window.scrollTo(0, startY + dist * easingFn(t));
        
        if (t < 1) {
            requestAnimationFrame(step);
        } else {
            opts.animatingRef.current = false;
            opts.onDone?.();
        }
    }

    requestAnimationFrame(step);
}

function getDocumentTop(el: HTMLElement | null) {
    let top = 0;
    
    while (el) {
        top += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
    }

    return top;
}

// hook

export interface ISnapOptions {
    duration?: number;
    easing?: 'cubic' | 'quart';
    heroCapturePx?: number; // downward capture zone near top
    upCapturePx?: number; // upward capture zone below hero bottom
    headerOffset?: number; // sticky header height
}

export function useHeroMainSnap(
    heroId: string,
    mainId: string,
    {
        duration = 900,
        easing = 'quart',
        heroCapturePx = 30,
        upCapturePx = 0,
        headerOffset = 0,
    }: ISnapOptions = {}
) {
    const animatingRef = useRef(false);
    const touchStartRef = useRef<number | null>(null);

    // snap helpers
    const snapDown = useCallback(() => {
        const target = document.getElementById(mainId);
        if (!target) {
            return;
        }

        const y = getDocumentTop(target) - headerOffset;
        
        animateWindowScrollTo(y, {
            duration, easing, animatingRef, onDone: () =>
                target.scrollIntoView({ behavior: "auto", block: "start" })
        });
    }, [mainId, duration, easing, headerOffset]);

    const snapUp = useCallback(() => {
        animateWindowScrollTo(0, { duration, easing, animatingRef });
    }, [duration, easing]);

    // zones
    const heroBottom = () => {
        const h = document.getElementById(heroId);
        return h ? getDocumentTop(h) + h.offsetHeight : null;
    };

    const withinUpZone = () => {
        const bottom = heroBottom();
        if (bottom == null) {
            return false;
        }

        const y = window.scrollY || window.pageYOffset;
        return y <= bottom + upCapturePx;
    };

    const heroVisible = () => {
        const h = document.getElementById(heroId);
        if (!h) {
            return false;
        }

        return h.getBoundingClientRect().bottom - headerOffset > 0;
    };

    // listeners
    const wheel = useCallback(function onHeroWheel(e: WheelEvent) {
        if (animatingRef.current) {
            e.preventDefault();
            return;
        }

        const y = window.scrollY || window.pageYOffset;
        if (e.deltaY > 0 && y <= heroCapturePx) {
            e.preventDefault();
            snapDown();
        } else if (e.deltaY < 0 && withinUpZone()) {
            e.preventDefault();
            snapUp();
        }
    }, [heroCapturePx, snapDown, snapUp, withinUpZone]);

    const key = useCallback((e: KeyboardEvent) => {
        if (animatingRef.current) {
            e.preventDefault();
            return;
        }

        const y = window.scrollY || window.pageYOffset;
        const downKeys = [" ", "PageDown", "ArrowDown"];
        const upKeys = ["PageUp", "ArrowUp"];

        if (downKeys.includes(e.key) && y <= heroCapturePx) {
            e.preventDefault(); snapDown();
        } else if (upKeys.includes(e.key) && (withinUpZone() || heroVisible())) {
            e.preventDefault(); snapUp();
        }
    }, [heroCapturePx, snapDown, snapUp]);

    const touchStart = (e: TouchEvent) => {
        touchStartRef.current = e.touches[0].clientY;
    };

    const touchMove = (e: TouchEvent) => {
        if (animatingRef.current) {
            e.preventDefault();
            return;
        }

        if (touchStartRef.current == null) {
            return;
        }

        const diff = touchStartRef.current - e.touches[0].clientY;
        const y = window.scrollY || window.pageYOffset;

        if (diff > 10 && y <= heroCapturePx) { // swipe up (scroll down)
            e.preventDefault(); snapDown();
        } else if (diff < -10 && (withinUpZone() || heroVisible())) { // swipe down
            e.preventDefault(); snapUp();
        }
    };

    // mount / unmount
    useEffect(() => {
        window.addEventListener("wheel", wheel, { passive: false });
        window.addEventListener("keydown", key, { passive: false });
        window.addEventListener("touchstart", touchStart, { passive: false });
        window.addEventListener("touchmove", touchMove, { passive: false });

        return () => {
            window.removeEventListener("wheel", wheel as any);
            window.removeEventListener("keydown", key as any);
            window.removeEventListener("touchstart", touchStart as any);
            window.removeEventListener("touchmove", touchMove as any);
        };
    }, [wheel, key]);
}
